import type { PvpParticipantId, PvpTeam } from '@kenocyber/shared';
import {
  ageCardSummon,
  CARD_DRONE_HEALTH_BPS,
  CARD_LEVIATHAN_ENTRY_BPS,
  CARD_LEVIATHAN_IDLE_MS,
  CARD_MAX_SUMMONS,
  type CardSummonKind,
} from '../cards/summons';
import { PVP_SHIP_RADIUS_MU } from './balance';

/**
 * **As entidades que uma carta põe no mundo** (gate V28H.L).
 *
 * Duas: o **Drone de Ataque** e o **Leviatã Aéreo**. Elas são a única coisa
 * deste sistema que existe FORA de um casco - tudo o resto que uma carta faz é
 * um temporizador sobre alguém, uma lâmina que orbita, ou um projétil.
 *
 * ## Porque um módulo próprio, e não um campo no casco
 *
 * Porque uma invocação tem posição, vida e um relógio seus, e porque ela pode
 * ser ATINGIDA. Um casco que carregasse o drone dentro dele teria de responder
 * "quem levou este tiro" com "eu ou a minha invocação", e essa é exactamente a
 * pergunta que o roster existe para não ter de ser feita.
 *
 * ## A regra de sobrevivência, decidida uma vez
 *
 * **Uma invocação morre com quem a invocou.** No tique em que o dono morre, se
 * desliga ou abandona, e no tique em que a partida acaba. É a regra mais
 * simples que é sempre verdadeira, e é a única que não deixa uma entidade órfã
 * numa instantânea - o que seria uma entidade que uma reconexão ressuscita e
 * ninguém controla.
 *
 * O Leviatã tem um caso a mais, e é **APROVADO**: uma vez que alguém entre, ele
 * é destruído **13 s** depois, aconteça o que acontecer.
 *
 * ## O que uma invocação NÃO é
 *
 * Não é um casco. Ela não tem cadeira, não tem cartas, não tem joias próprias,
 * não conta para o fim da partida, e não pode ganhar nem perder um duelo. Uma
 * equipa cujos cascos morreram perdeu, esteja o drone dela onde estiver.
 */

/**
 * O que esta entidade é.
 *
 * O tipo vive em `cards/summons.ts` desde o gate V28H.M: a caça passou a ter as
 * mesmas duas, e o que é da CARTA não pode viver dentro de um modo.
 */
export type PvpSummonKind = CardSummonKind;

/**
 * Uma invocação a existir na arena.
 *
 * Tudo inteiro menos a vida, que é `bigint` como toda a vida deste jogo desde o
 * gate V28G.S.1 - a progressão não tem tecto, e um drone com 30% da vida de um
 * casco herda essa ausência de tecto.
 */
export interface PvpSummonState {
  readonly id: number;
  readonly kind: PvpSummonKind;
  /** Quem a invocou. As joias dela são as DELE, e ela morre com ele. */
  readonly ownerParticipantId: PvpParticipantId;
  readonly ownerTeam: PvpTeam;
  x: number;
  y: number;
  /** Para onde ela aponta, normalizado. O desenho roda por isto. */
  aimX: number;
  aimY: number;
  healthMilli: bigint;
  readonly maxHealthMilli: bigint;
  /**
   * Milissegundos de vida por gastar.
   *
   * No Drone é o tempo dele. No Leviatã é o tempo que ele espera por um piloto:
   * quando alguém entra, este contador é substituído pelos 13 s aprovados, e é
   * `boarded` que passa a mandar.
   */
  lifeMs: number;
  /** O relógio de disparo desta entidade. Próprio, e nunca o do dono. */
  attackAccumulatorMs: number;
  /**
   * Quem pilota, ou `null`. **Só o Leviatã.**
   *
   * Um id e não um casco: a entidade não segura uma referência para uma nave,
   * porque uma instantânea não consegue serializar uma referência.
   */
  pilotParticipantId: PvpParticipantId | null;
  /**
   * Milissegundos desde a ENTRADA, a contar para trás. **APROVADO: 13 s.**
   *
   * `0` enquanto ninguém entrou. Depois de alguém entrar, é este contador que
   * decide o fim - e não `lifeMs` - porque o contrato é explícito: a destruição
   * é automática treze segundos após a entrada, aconteça o que acontecer.
   */
  boardedRemainingMs: number;
}

/** Quantas invocações uma sala aguenta. O tecto neutro, com o nome do duelo. */
export const PVP_MAX_SUMMONS = CARD_MAX_SUMMONS;

/**
 * A vida de um Drone, em milli: uma fracção da do dono.
 *
 * Do DONO e nunca um número absoluto, pela mesma razão que o dano de uma carta
 * é uma proporção: um drone com vida fixa seria descartável no fim da
 * progressão e imortal no princípio dela.
 */
export const PVP_DRONE_HEALTH_BPS = CARD_DRONE_HEALTH_BPS;

/**
 * A que distância um casco entra num Leviatã, em bps do raio do casco DELE.
 *
 * "Entrada física da nave" é uma decisão aprovada, e isto é a metade que a
 * simulação consegue afirmar: encostar entra. As entradas laterais e as setas
 * animadas são desenho, e vivem na cena.
 */
export const PVP_LEVIATHAN_ENTRY_BPS = CARD_LEVIATHAN_ENTRY_BPS;

/**
 * **Quão maior do que o casco do dono é a estrutura**, e o chão dela.
 *
 * Os dois números viviam na cena, que é onde eles não podem viver a partir do
 * gate V28H.X: agora a sala usa-os para pôr as seis bocas, e a cena desenha os
 * seis canos nas mesmas fracções. Um cano desenhado onde o tiro não sai é uma
 * mentira que se vê ao primeiro disparo.
 */
export const PVP_LEVIATHAN_SIZE_FACTOR = 3.5;
export const PVP_LEVIATHAN_MIN_HULLS = 4;

/**
 * Quanto tempo um Leviatã espera por um piloto antes de se desfazer.
 *
 * **Provisório.** Os 13 s de pilotagem são aprovados; quanto ele espera não é, e
 * este número existe para ele não ficar na arena para sempre se ninguém entrar.
 */
export const PVP_LEVIATHAN_IDLE_MS = CARD_LEVIATHAN_IDLE_MS;

/**
 * **Quantos canhões um Leviatã tem, e onde estão as bocas** (gate V28H.X).
 *
 * ## O defeito que isto corrige
 *
 * "Seis canhões" é literal no contrato aprovado, e a sala cumpria o NÚMERO sem
 * cumprir os canhões: disparava seis tiros da mesma boca, no mesmo tique, com a
 * mesma direcção. Seis projécteis exactamente sobrepostos leem-se como um, e o
 * que o dono via era uma estrutura enorme a cuspir um fio de tiros.
 *
 * Agora as bocas são seis pontos diferentes ao longo da frente, e os seis tiros
 * nascem em sítios diferentes. O dano não muda - continuam a ser seis - o que
 * muda é que agora se vêem seis.
 *
 * ## Porque a geometria vive aqui e não na cena
 *
 * Porque ela decide onde um tiro NASCE, e isso é mecânica. A cena desenha os
 * canos nos mesmos pontos, lidos desta função - é a disciplina dos Super
 * Canhões, e a razão é a mesma: um cano desenhado onde o tiro não sai é uma
 * mentira que se vê ao primeiro disparo.
 */
export const PVP_LEVIATHAN_BARRELS = 6;

/**
 * **Onde estão os seis canos, em fracções do raio do corpo.**
 *
 * Fracções e não distâncias, porque quem desenha e quem dispara medem em
 * unidades diferentes - a cena em píxeis do mundo, a sala em unidades da arena -
 * e a única coisa que os dois podem partilhar sem converter nada é a FORMA.
 *
 * Três de cada lado, e os de fora avançam menos: a frente de um veículo é uma
 * proa, e seis canos alinhados a régua leriam-se como uma grelha.
 */
export const PVP_LEVIATHAN_MUZZLE_LAYOUT: readonly {
  readonly lateral: number;
  readonly forward: number;
}[] = Object.freeze(
  [-1, 1].flatMap((sign) =>
    [0.22, 0.46, 0.7].map((step) => ({
      lateral: step * sign,
      forward: 1.02 - step * 0.28,
    })),
  ),
);

/**
 * **O raio do corpo de um Leviatã**, em unidades da arena.
 *
 * Três vezes e meia o raio do casco de quem o invocou, com um chão: "tamanho
 * mínimo grande" é decisão aprovada, e a razão dela é física - uma nave tem de
 * caber lá dentro para a entrada fazer sentido.
 */
export function pvpLeviathanBodyRadiusMu(ownerHullRadiusMu: number): number {
  return Math.max(
    PVP_SHIP_RADIUS_MU * PVP_LEVIATHAN_MIN_HULLS,
    Math.trunc(ownerHullRadiusMu * PVP_LEVIATHAN_SIZE_FACTOR),
  );
}

/** As seis bocas, em unidades da arena, na ordem canónica. */
export function pvpLeviathanMuzzles(
  summon: Pick<PvpSummonState, 'x' | 'y' | 'aimX' | 'aimY'>,
  bodyRadiusMu: number,
): readonly { readonly x: number; readonly y: number }[] {
  const length = Math.hypot(summon.aimX, summon.aimY);
  if (length === 0) {
    return [];
  }
  const forwardX = summon.aimX / length;
  const forwardY = summon.aimY / length;
  /* A perpendicular, à esquerda da mira. A outra é o seu simétrico. */
  const sideX = -forwardY;
  const sideY = forwardX;
  return PVP_LEVIATHAN_MUZZLE_LAYOUT.map((place) => {
    const lateral = bodyRadiusMu * place.lateral;
    const forward = bodyRadiusMu * place.forward;
    return {
      x: Math.round(summon.x + sideX * lateral + forwardX * forward),
      y: Math.round(summon.y + sideY * lateral + forwardY * forward),
    };
  });
}

/** Se esta entidade tem alguém aos comandos. */
export function pvpSummonIsPiloted(summon: PvpSummonState): boolean {
  return summon.pilotParticipantId !== null;
}

/**
 * Faz correr os relógios de uma invocação, e diz se ela ACABOU.
 *
 * Um passo, e a mesma função nos dois modos. Devolve `true` quando esta
 * entidade deve sair da arena neste tique - por tempo, por vida, ou pelos treze
 * segundos aprovados do Leviatã.
 */
export function pvpAgeSummon(summon: PvpSummonState, stepMs: number): boolean {
  /*
   * A vida é do DUELO - uma invocação pode ser abatida lá - e o resto do relógio
   * é da carta, partilhado com a caça desde o gate V28H.M.
   */
  if (summon.healthMilli <= 0n) {
    return true;
  }
  return ageCardSummon(summon, stepMs);
}
